import type { ChangelogModalProps, VersionType } from './const';
import { I18nConsumer, intl } from '@wegic/i18n-core';
import { IconClose, IconInfo } from '@wegic/icon';
import { Button, Divider, Radio, ScrollArea } from '@wegic/ui';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import styles from './ChangelogModal.module.less';
import { getUserConfig, RightBundle } from './const';
import { CHANGELOG_I18N_NAMESPACE, changelogI18nLoaders } from './i18n/loaders';

function ChangelogModalContent({
  visible,
  onCancel,
  onConfirm,
  userProductKey,
  showDebugPanel = false,
}: ChangelogModalProps) {
  const [selectedVersion, setSelectedVersion] = useState<VersionType | null>('v2');

  // Debug 状态汇总
  const [debug, setDebug] = useState({
    previewUserType: null as RightBundle | null,
    mobilePreview: false,
  });

  // 检测是否为移动端
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Get user type config with fallback mapping
  const productKey = debug.previewUserType || userProductKey;
  const userConfig = getUserConfig(productKey);

  // 实际使用的移动端判断: Debug模式优先,否则使用真实的移动端检测
  const isActualMobile = debug.mobilePreview || isMobile;

  // Reset selection when dialog is opened
  useEffect(() => {
    if (visible) {
      setSelectedVersion('v2');
    }
  }, [visible]);

  // Lock body scroll when dialog is open
  useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [visible]);

  const handleV2Click = () => {
    setSelectedVersion('v2');
  };

  const handleV1Click = () => {
    setSelectedVersion('v1');
  };

  const handleConfirm = () => {
    if (selectedVersion) {
      onConfirm(selectedVersion);
    }
  };

  const getButtonText = () => {
    if (!selectedVersion) {
      return intl.get('changelog.continueButton');
    }
    return selectedVersion === 'v2' ? intl.get('changelog.continueButtonV2') : intl.get('changelog.continueButtonV1');
  };

  if (!visible) {
    return null;
  }

  return createPortal(
    <div className={styles.overlay} onClick={onCancel}>
      <div
        className={`${styles.modal} ${isActualMobile ? styles['mobile-preview'] : styles['desktop-modal']}`}
        onClick={e => e.stopPropagation()}
      >
        {/* 关闭按钮 */}
        <Button
          type="secondary"
          size="small"
          className={styles['close-button']}
          onClick={onCancel}
          icon={<IconClose fontSize={16} />}
        />
        {/* Debug Panel */}
        {showDebugPanel && (
          <div className={styles['preview-switcher']}>
            <button
              className={`${styles['preview-button']} ${!debug.previewUserType ? styles.active : ''}`}
              onClick={() => setDebug({ ...debug, previewUserType: null })}
            >
              Current
            </button>
            <button
              className={`${styles['preview-button']} ${debug.previewUserType === RightBundle.FREE ? styles.active : ''}`}
              onClick={() => setDebug({ ...debug, previewUserType: RightBundle.FREE })}
            >
              FREE
            </button>
            <button
              className={`${styles['preview-button']} ${debug.previewUserType === RightBundle.STARTER ? styles.active : ''}`}
              onClick={() => setDebug({ ...debug, previewUserType: RightBundle.STARTER })}
            >
              STARTER
            </button>
            <button
              className={`${styles['preview-button']} ${debug.previewUserType === RightBundle.PREMIUM ? styles.active : ''}`}
              onClick={() => setDebug({ ...debug, previewUserType: RightBundle.PREMIUM })}
            >
              PREMIUM
            </button>
            <span className={styles['divider-line']} />
            <button
              className={`${styles['preview-button']} ${debug.mobilePreview ? styles.active : ''}`}
              onClick={() => setDebug({ ...debug, mobilePreview: !debug.mobilePreview })}
            >
              📱 Mobile
            </button>
          </div>
        )}

        {isActualMobile ? (
          <ScrollArea className={styles['modal-content']} variant="light">
            <div style={{ padding: 20 }}>
              {/* Left Panel - Information */}
              <div className={styles['left-panel']}>
                <div className={styles.header}>
                  <p className={styles['reward-label']}>{intl.get('changelog.rewardLabel')}</p>
                  <h1 className={styles.title}>{intl.get('changelog.title')}</h1>
                </div>

                <p
                  className={styles.description}
                  dangerouslySetInnerHTML={{
                    __html: intl.get(userConfig.descriptionKey),
                  }}
                />

                <div className={styles['info-card']}>
                  <IconInfo fontSize={16} className={styles['info-icon']} />
                  <p>{intl.get('changelog.infoIndependent')}</p>
                </div>

                <div className={styles.benefits}>
                  <p className={styles['benefits-title']}>{intl.get('changelog.benefitsTitle')}</p>
                  <ul className={styles['benefits-list']}>
                    {userConfig.benefits.map((benefit, index) => (
                      <li key={index}>
                        <strong>{intl.get(benefit.titleKey)}</strong>
                        {benefit.detailKey && (
                          <>
                            {' '}
                            <span className={styles['benefits-detail']}>{intl.get(benefit.detailKey)}</span>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Divider */}
              <Divider direction="horizontal" theme="light" className={styles.divider} />

              {/* Right Panel - Version Cards */}
              <div className={styles['right-panel']}>
                <div
                  className={`${styles['version-card']} ${selectedVersion === 'v1' ? styles['highlight-card'] : ''}`}
                  onClick={handleV1Click}
                >
                  <div className={styles['card-header']}>
                    <div className={`${styles.badge} ${styles['classic-badge']}`}>{intl.get('changelog.v1Badge')}</div>
                    <Radio checked={selectedVersion === 'v1'} theme="light" />
                  </div>
                  <div className={styles['card-content']}>
                    <div className={styles['version-header']}>
                      <h3 className={styles['version-title']}>{intl.get('changelog.v1Title')}</h3>
                      <p className={styles['version-subtitle']}>{intl.get('changelog.v1Subtitle')}</p>
                    </div>
                    <ul className={styles['features-list']}>
                      <li>{intl.get('changelog.v1Feature1')}</li>
                      <li>{intl.get('changelog.v1Feature2')}</li>
                      <li>{intl.get('changelog.v1Feature3')}</li>
                    </ul>
                  </div>
                </div>

                <div
                  className={`${styles['version-card']} ${selectedVersion === 'v2' ? styles['highlight-card'] : ''}`}
                  onClick={handleV2Click}
                >
                  <div className={styles['card-header']}>
                    <div className={`${styles.badge} ${selectedVersion === 'v2' ? styles['highlight-badge'] : ''}`}>
                      {intl.get('changelog.v2Badge')}
                    </div>
                    <Radio checked={selectedVersion === 'v2'} theme="light" />
                  </div>
                  <div className={styles['card-content']}>
                    <div className={styles['version-header']}>
                      <h3 className={styles['version-title']}>{intl.get('changelog.v2Title')}</h3>
                      <p className={styles['version-subtitle']}>{intl.get('changelog.v2Subtitle')}</p>
                    </div>
                    <ul className={styles['features-list']}>
                      <li>{intl.get('changelog.v2Feature2')}</li>
                      <li>{intl.get('changelog.v2Feature3')}</li>
                      <li>{intl.get('changelog.v2Feature4')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>
        ) : (
          <div className={styles['modal-content']}>
            {/* Left Panel - Information */}
            <ScrollArea className={styles['left-panel']} variant="light">
              <div className={styles.header}>
                <p className={styles['reward-label']}>{intl.get('changelog.rewardLabel')}</p>
                <h1 className={styles.title}>{intl.get('changelog.title')}</h1>
              </div>

              <p
                className={styles.description}
                dangerouslySetInnerHTML={{
                  __html: intl.get(userConfig.descriptionKey),
                }}
              />

              <div className={styles['info-card']}>
                <IconInfo fontSize={16} className={styles['info-icon']} />
                <p>{intl.get('changelog.infoIndependent')}</p>
              </div>

              <div className={styles.benefits}>
                <p className={styles['benefits-title']}>{intl.get('changelog.benefitsTitle')}</p>
                <ul className={styles['benefits-list']}>
                  {userConfig.benefits.map((benefit, index) => (
                    <li key={index}>
                      <strong>{intl.get(benefit.titleKey)}</strong>
                      {benefit.detailKey && (
                        <>
                          {' '}
                          <span className={styles['benefits-detail']}>{intl.get(benefit.detailKey)}</span>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollArea>

            {/* Divider */}
            <Divider direction="vertical" theme="light" className={styles.divider} />

            {/* Right Panel - Version Cards */}
            <div className={styles['right-panel']}>
              {/* 桌面端：统一滚动区域 */}
              <ScrollArea className={styles['version-cards-container']} variant="light">
                <div
                  className={`${styles['version-card']} ${selectedVersion === 'v2' ? styles['highlight-card'] : ''}`}
                  onClick={handleV2Click}
                >
                  <div className={styles['card-header']}>
                    <div className={`${styles.badge} ${selectedVersion === 'v2' ? styles['highlight-badge'] : ''}`}>
                      {intl.get('changelog.v2Badge')}
                    </div>
                    <Radio checked={selectedVersion === 'v2'} theme="light" />
                  </div>
                  <div className={styles['card-content']}>
                    <div className={styles['version-header']}>
                      <h3 className={styles['version-title']}>{intl.get('changelog.v2Title')}</h3>
                      <p className={styles['version-subtitle']}>{intl.get('changelog.v2Subtitle')}</p>
                    </div>
                    <ul className={styles['features-list']}>
                      <li>{intl.get('changelog.v2Feature2')}</li>
                      <li>{intl.get('changelog.v2Feature3')}</li>
                      <li>{intl.get('changelog.v2Feature4')}</li>
                    </ul>
                  </div>
                </div>

                <div
                  className={`${styles['version-card']} ${selectedVersion === 'v1' ? styles['highlight-card'] : ''}`}
                  onClick={handleV1Click}
                  style={{ marginTop: '30px' }}
                >
                  <div className={styles['card-header']}>
                    <div className={`${styles.badge} ${styles['classic-badge']}`}>{intl.get('changelog.v1Badge')}</div>
                    <Radio checked={selectedVersion === 'v1'} theme="light" />
                  </div>
                  <div className={styles['card-content']}>
                    <div className={styles['version-header']}>
                      <h3 className={styles['version-title']}>{intl.get('changelog.v1Title')}</h3>
                      <p className={styles['version-subtitle']}>{intl.get('changelog.v1Subtitle')}</p>
                    </div>
                    <ul className={styles['features-list']}>
                      <li>{intl.get('changelog.v1Feature1')}</li>
                      <li>{intl.get('changelog.v1Feature2')}</li>
                      <li>{intl.get('changelog.v1Feature3')}</li>
                    </ul>
                  </div>
                </div>
              </ScrollArea>

              {/* Continue Button - 桌面端 */}
              <Button
                type="primary"
                size="default"
                status="plain"
                className={styles['continue-button']}
                onClick={handleConfirm}
                disabled={!selectedVersion}
                block
              >
                {getButtonText()}
              </Button>
            </div>
          </div>
        )}

        {/* Continue Button - 移动端固定在底部 */}
        {isActualMobile && (
          <Button
            type="primary"
            size="default"
            status="plain"
            className={styles['mobile-confirm-button']}
            onClick={handleConfirm}
            disabled={!selectedVersion}
          >
            {getButtonText()}
          </Button>
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * ChangelogModal - 带国际化加载的变更日志弹窗
 */
function ChangelogModal(props: ChangelogModalProps) {
  return (
    <I18nConsumer loaders={changelogI18nLoaders} namespace={CHANGELOG_I18N_NAMESPACE}>
      <ChangelogModalContent {...props} />
    </I18nConsumer>
  );
}

export default ChangelogModal;
